import { createFileRoute, Link } from "@tanstack/react-router";
import { ArrowRight } from "lucide-react";
import { AuroraBackground } from "@/components/aurora-background";
import { SectionEyebrow } from "@/components/section-eyebrow";
import { comparisons } from "@/lib/comparisons-data";
import { seo } from "@/lib/seo";
import { collectionPage, breadcrumb } from "@/lib/structured-data";
import { SITE_URL } from "@/lib/seo-constants";

export const Route = createFileRoute("/comparisons/")({
  head: () =>
    seo({
      title: "Axy.digital vs. Marketing Tools & Agencies",
      description:
        "How Axy.digital compares to Jasper, Clearscope, Surfer, Writesonic, Taplio, Sprout Social, traditional agencies, DIY agent stacks and more.",
      path: "/comparisons",
      jsonLd: [
        collectionPage({
          name: "Axy.digital Comparisons",
          description: "Side-by-side breakdowns of Axy.digital vs the most common alternatives.",
          url: `${SITE_URL}/comparisons`,
          items: comparisons.map((c) => ({
            name: c.seoTitle,
            url: `/comparisons/${c.slug}`,
          })),
        }),
        breadcrumb([
          { name: "Home", url: "/" },
          { name: "Comparisons", url: "/comparisons" },
        ]),
      ],
    }),
  component: ComparisonsIndex,
});

const groups = [
  {
    title: "AI marketing platforms",
    slugs: [
      "axy-vs-blaze-ai",
      "axy-vs-getdojo-ai",
      "axy-vs-jasper",
      "axy-vs-copy-ai",
      "axy-vs-hubspot",
    ],
  },
  {
    title: "SEO, GEO & AI search",
    slugs: [
      "axy-vs-clearscope",
      "axy-vs-surfer-seo",
      "axy-vs-airops",
      "axy-vs-writesonic",
      "axy-vs-gauge",
    ],
  },
  {
    title: "Social & LinkedIn",
    slugs: ["axy-vs-sprout-social", "axy-vs-taplio"],
  },
  {
    title: "GTM systems & alternatives",
    slugs: [
      "axy-vs-coldiq",
      "axy-vs-workflows-io",
      "axy-vs-traditional-agency",
      "axy-vs-diy-agents",
    ],
  },
];

function ComparisonsIndex() {
  return (
    <section className="relative px-6 py-24 overflow-hidden">
      <AuroraBackground />
      <div className="relative max-w-7xl mx-auto">
        <SectionEyebrow>Comparisons</SectionEyebrow>
        <h1 className="text-4xl md:text-6xl font-semibold tracking-tight text-white mb-6 text-balance">
          Axy.digital versus the <span className="text-gradient">alternatives.</span>
        </h1>
        <p className="text-lg text-white/70 max-w-2xl">
          Honest, side-by-side breakdowns of how the Axy.digital engine compares to the tools and
          services teams typically consider.
        </p>

        <div className="mt-14 space-y-14">
          {groups.map((g) => (
            <div key={g.title}>
              <p className="text-[10px] font-medium uppercase tracking-[0.22em] text-pink-soft mb-5">
                {g.title}
              </p>
              <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
                {g.slugs.map((slug) => {
                  const c = comparisons.find((x) => x.slug === slug);
                  if (!c) return null;
                  return (
                    <Link
                      key={c.slug}
                      to="/comparisons/$slug"
                      params={{ slug: c.slug }}
                      className="group relative glass-card p-6 hover-lift hover:-translate-y-1 hover:border-white/25 overflow-hidden transition-all duration-300"
                    >
                      <div
                        aria-hidden
                        className="absolute -top-12 -right-12 size-36 rounded-full blur-3xl opacity-0 group-hover:opacity-70 transition-opacity duration-500"
                        style={{
                          background:
                            "radial-gradient(circle, rgba(217,70,239,0.55), transparent 65%)",
                        }}
                      />
                      <p className="relative text-[10px] uppercase tracking-[0.22em] text-white/40 mb-2">
                        Axy.digital vs
                      </p>
                      <h3 className="relative text-xl font-semibold text-white mb-3">
                        {c.competitor}
                      </h3>
                      <p className="relative text-sm text-white/65 mb-5 line-clamp-2">
                        {c.heroSubtitle}
                      </p>
                      <span className="relative inline-flex items-center gap-1 text-xs text-pink-soft group-hover:gap-2 transition-all">
                        Read comparison <ArrowRight className="size-3.5" />
                      </span>
                    </Link>
                  );
                })}
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}
